import { Method, RequestConfig } from "@emed-labs/platform-utility-generic-clients";
import { LoveClient } from "@emed-labs/platform-utility-generic-clients";
import axios, { AxiosRequestConfig } from "axios";
import { PropsWithChildren, createContext, use, useContext, useEffect, useRef } from "react";
import { BrowserUtils } from "../utils/BrowserUtils";
import { EnvironmentContext } from "./EnvironmentProvider";
import { Config } from "../environment/config";
import { useLogger } from "./LogProvider";

export interface VLabAPIContext {
	call: <Request_1 = any, Response_1 = any>(
		method: Method,
		endpoint: string,
		config?: RequestConfig<Request_1>
	) => Promise<Response_1>;
	getAPIBaseUrl: () => string;
	loadFileFromVLabScriptsCDN: <T = any>(cdnFilePath: string, disableCache?: boolean) => Promise<T>;
	fileExistsOnVLabScriptsCDN: (cdnFilePath: string) => Promise<boolean>;
}

/**
 * Hides UI and shows a spinner
 * @param props
 * @returns
 */
export const VLabAPIContext = createContext<VLabAPIContext>({} as VLabAPIContext);
VLabAPIContext.displayName = "VLabAPIContext";

export const VLabAPIProvider: React.FunctionComponent<PropsWithChildren> = (props) => {
	const EnvCtx = useContext(EnvironmentContext);
	const logger = useLogger("providers.VLabAPIProvider");
	const getAPIBaseUrl = (): string => {
		//VLAB_API_URL for local development. Wont be available on server
		const vlabApiUrl = EnvCtx.get(Config.VLAB_API_URL, { optional: true });

		if (!vlabApiUrl) {
			return `${BrowserUtils.getOrigin()}/api`;
		}

		// If the URL is relative (doesn't start with http:// or https://), prepend the origin
		if (!vlabApiUrl.startsWith("http://") && !vlabApiUrl.startsWith("https://")) {
			return `${BrowserUtils.getOrigin()}${vlabApiUrl.startsWith("/") ? "" : "/"}${vlabApiUrl}`;
		}

		return vlabApiUrl;
	};

	const setupClient = () => {
		VLAB_API_CLIENT.current = new LoveClient(`${getAPIBaseUrl()}`, {
			timeout: 15 * 1000,
			debug: EnvCtx.get(Config.FLAG_HTTP_CLIENT_DEBUG, { optional: true }),
			withCredentials: true
		});
	};

	const VLAB_API_CLIENT = useRef<LoveClient>();
	setupClient();

	useEffect(() => {
		setupClient();
	});

	const getVLabScriptsCDNUrl = (cdnFilePath: string): string => {
		return `${EnvCtx.get(Config.SCRIPTS_BASE_URL)}${cdnFilePath}`;
	};

	const loadFileFromVLabScriptsCDN = async <T = any,>(
		cdnFilePath: string,
		disableCache: boolean = false
	): Promise<T> => {
		const config: AxiosRequestConfig = {};

		if (disableCache) {
			config.headers = {
				"Cache-Control": "no-cache",
				"Pragma": "no-cache",
				"Expires": "0"
			};
		}

		return (await axios.get(getVLabScriptsCDNUrl(cdnFilePath), config)).data;
	};

	const fileExistsOnVLabScriptsCDN = async (cdnFilePath: string) => {
		try {
			const response = await axios.head(getVLabScriptsCDNUrl(cdnFilePath));
			return response.status <= 299;
		} catch (e) {
			logger.error("fileExistsOnVLabScriptsCDN", "fileExistsOnCDN / axios head error", e, {
				cdnFilePath,
				fullCdnFilePath: getVLabScriptsCDNUrl(cdnFilePath)
			});
		}

		return false;
	};

	const call = <Request_1 = any, Response_1 = any>(
		method: Method,
		endpoint: string,
		config?: RequestConfig<Request_1>
	) => {
		if (!VLAB_API_CLIENT.current) {
			setupClient();
		}
		return VLAB_API_CLIENT.current!.call(method, endpoint, config) as Response_1;
	};

	const value: VLabAPIContext = {
		call,
		getAPIBaseUrl,
		loadFileFromVLabScriptsCDN,
		fileExistsOnVLabScriptsCDN
	};
	return <VLabAPIContext.Provider value={value}>{props.children}</VLabAPIContext.Provider>;
};
